import React, { useMemo, useState } from "react";
import { Cell } from "fixed-data-table-2";
import { Form, Modal, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faCog,
  faFileSignature,
} from "@fortawesome/free-solid-svg-icons";
import { formatDate } from "../../../../../../utils/DateTimeUtils";
import {
  EvidenceColumn,
  EvidenceColumnGroup,
  EvidenceType,
  PopupDismissalKeys,
} from "../../../../../../../store/onboarding/types";
import { getDisplayedLabel } from "./AssessmentLabelTooltip";
import {
  evidenceColumnDisplayTitle,
  evidenceColumnGroupDisplayTitle,
} from "../Helpers";
import Popover from "react-bootstrap/Popover";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import {
  openEquityBenchmarksModal,
  openEvidenceConfirmDeletionModal,
} from "../../../../../../../store/onboarding/actions";
import { useDispatch, useSelector } from "react-redux";
import { faTimesCircle } from "@fortawesome/free-regular-svg-icons";
import { ApplicationState } from "../../../../../../../store";
import { showConfirmDialog } from "../../../../../../../store/confirm-dialog/actions";
import { isShowConfimationDialog } from "../../../../../../../utils/NamesUtils";

type Props = {
  isReadonly?: boolean;
  evidenceColumnGroup: EvidenceColumnGroup;
  numberDisplayedColumns: number;
  onDisplayedColumnChange: (
    evidenceColumnGroupId: number,
    evidenceColumnId: number
  ) => any;
  checkDisplayedColumn: (
    evidenceColumnGroupId: number,
    evidenceColumnId: number
  ) => boolean;
  deleteEvidenceColumnsGroup: (evidenceColumnGroupId: number) => boolean;
};

const ASSESSMENT_DISPLAYED_CHARACTERS_NUM = 25;
const replaceStringLength = 3;

const AssessmentHeaderCell = (props: Props) => {
  const {
    evidenceColumnGroup,
    numberDisplayedColumns,
    checkDisplayedColumn,
    onDisplayedColumnChange,
    deleteEvidenceColumnsGroup,
  } = props;
  const [showMeasurementDropDown, setShowMeasurementDropDown] = useState(false);
  const partialColumnsDisplay =
    evidenceColumnGroup.evidence_columns.some(
      (ec) => ec.evidence_type === EvidenceType.Data
    ) &&
    (evidenceColumnGroup.evidence_columns.length > 1) &&
    (evidenceColumnGroup.evidence_columns.length !== numberDisplayedColumns);

  const dispatch = useDispatch();

  //const currentDataPeriod = useSelector((s: ApplicationState) => s.onboarding.currentDataPeriod);
  const currentUser = useSelector((s: ApplicationState) => s.auth.userInfo);
  
  const isAccessToDelete = useMemo(() => { //RC-1411
    return evidenceColumnGroup?.user_id == currentUser?.id 
  },[evidenceColumnGroup])

  const isCustomAssessment = useMemo(() => {
    return evidenceColumnGroup.assessment?.is_system_assessment === false;
  }, [evidenceColumnGroup]);

  const isEvidenceTypeDataOnly = useMemo(() => {
    return evidenceColumnGroup.evidence_columns.every(
      (ec) => ec.evidence_type === EvidenceType.Data
    );
  }, [evidenceColumnGroup]);

  const userDismissPopupDetails =
    useSelector(
      (s: ApplicationState) => s.auth.userInfo?.user_dismiss_pop_up_details || {}
    );
  const handleDeleteEvidenceCG = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(
      showConfirmDialog({
        onConfirm: () => {
          if (evidenceColumnGroup) {
            deleteEvidenceColumnsGroup(evidenceColumnGroup.id);
          }
        },
        centered: true,
        title: 'Delete Assessment Table',
        timerElipseLeft: true,
        //countDownTimer: 3,
        text: (
          <div className="text-center">
            <p className="font-weight-bold">Are you sure you want to delete this assessment data table?</p>
            <p>This data may be shared with other users who might have used it. If you proceed, they could lose access.</p>
          </div>
        ),
        confirmButtonText: "DELETE",
      })
    );
    // dispatch(openEvidenceConfirmDeletionModal({
    //   evidenceColumnGroup : evidenceColumnGroup, 
    //   timeElipse: true
    // }));
  };
  

  const assessemntLabelDropdown = (evidenceColumnGroup: EvidenceColumnGroup) => {

    let numberDisplayedColumns = 0;
    evidenceColumnGroup.evidence_columns.forEach((ec) => {
      if (checkDisplayedColumn(evidenceColumnGroup.id, ec.id)) {
        numberDisplayedColumns += 1;
      }
    });

    let assessmentName = getDisplayedLabel(
      evidenceColumnGroupDisplayTitle(evidenceColumnGroup),
      ASSESSMENT_DISPLAYED_CHARACTERS_NUM,
      numberDisplayedColumns,
      isCustomAssessment,
      isEvidenceTypeDataOnly
    );

    const assessmentLabelData = <div>
      {
        <>
          <div className="assessmentLabelName" style={{cursor:"default"}}>
            <div className={`mt-2`}
            >
              {props.isReadonly ? (
                <span>
                  {assessmentName}
                </span>
              ): (
                <span onClick={() => setShowMeasurementDropDown(!showMeasurementDropDown)} className="pointer">
                  <span className="mr-1" >
                    <FontAwesomeIcon icon={faCog} />
                  </span>
                  {assessmentName}
                </span>
              )}
            </div>
            {partialColumnsDisplay && (
              <div className="partialColumnsDisplay mb-1">
                <span onClick={() => setShowMeasurementDropDown(!showMeasurementDropDown)}>
                  (Displaying {numberDisplayedColumns} of{" "}
                  {evidenceColumnGroup.evidence_columns.length} columns)
                </span>
              </div>
            )}
          </div>
        </>

      }
    </div>
    return <div className={`assessmentLabelDropdown ${props.isReadonly ? 'default-pointer' : ''}`}>
             <div className="assessmentLabelDetails">
                {assessmentLabelData}
                <div className="assessmentLabelDate mt-1 default-pointer">
                  <FontAwesomeIcon icon={faFileSignature} />
                  {formatDate(evidenceColumnGroup.created)}
                </div>
              </div>
              <Modal
                show={showMeasurementDropDown}
                onHide={() => setShowMeasurementDropDown(false)}
                animation={false}
                backdropClassName="customDarkModalBackdrop in"
                centered
              >
                <Modal.Header
                  closeButton
                  className={"purpleModalHeader"}
                >
                  <Modal.Title>Show/Hide Columns</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                {!props.isReadonly && evidenceColumnGroup &&(
                  <>
                    <label>Check or uncheck columns to show or hide them in the table.</label>
                    <hr/> 
                    {evidenceColumnGroup.evidence_columns
                    .sort((a: EvidenceColumn, b: EvidenceColumn) => (a.id < b.id) ? -1 : 1)
                    .map((
                      evidenceColumn: EvidenceColumn,
                      index
                    ) => (
                      <div
                        key={index}
                        className="d-flex align-items-center mb-2"
                      >
                        <Form.Check
                          readOnly
                          custom
                          id={`evidence-column-${evidenceColumn.id}`}
                          type="checkbox"
                          className="evidenceGroupItemCheckbox"
                          checked={checkDisplayedColumn(
                            evidenceColumnGroup.id,
                            evidenceColumn.id
                          )}
                          onClick={onDisplayedColumnChange(
                            evidenceColumnGroup.id,
                            evidenceColumn.id
                          )}
                        /> 
                        <label className="pointer" htmlFor={`evidence-column-${evidenceColumn.id}`}>{evidenceColumnDisplayTitle(evidenceColumn)}</label>
                      </div>
                      )
                    )} 
                    {!isCustomAssessment && evidenceColumnGroup.assessment && (
                        <>
                          <hr/>
                          <div className="text-center">
                            <button
                              className="blueBtnSm my-2"
                              onClick={() => {
                                document.body.click();
                                setShowMeasurementDropDown(false)
                                //RC-1963 backgrond assessment list must be disappear.
                                dispatch(
                                  openEquityBenchmarksModal(evidenceColumnGroup.id)
                                );
                              }}
                            >
                              Show Equity Benchmarks
                            </button>
                          </div>
                        </>
                    )}
                </>
                )}
                </Modal.Body>
              </Modal>
          </div>
  }

  return (
    <Cell className="assessmentLabel">
      {assessemntLabelDropdown(evidenceColumnGroup)}
      {!props.isReadonly && isAccessToDelete &&(
        <span
          className="mx-2 removeIconBtn"
          onClick={handleDeleteEvidenceCG}
        >
          <FontAwesomeIcon
            icon={faTimesCircle}
            color="red"
            size="lg"
          />
        </span>
      )}
    </Cell>
  );
};

export default AssessmentHeaderCell;
